import React from "react";
import { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import "./Userstyles/shipping.css";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "../../../db/Firebase";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

function Address() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shippingInfo, setShippingInfo] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [newShippingInfo, setNewShippingInfo] = useState({
    name: "",
    number: "",
    flat: "",
    street: "",
    locality: "",
    city: "",
    state: "",
    pinCode: "",
    email: "",
  });

  const fetchShippingInfo = async (uid) => {
    try {
      const shippingCollection = collection(db, "users", uid, "ShippingInfo");
      const shippingSnapshot = await getDocs(shippingCollection);
      const shippingData = shippingSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShippingInfo(shippingData);
    } catch (error) {
      console.error("Error fetching shipping info:", error.message);
    }
  };

  const enableEditing = (index) => {
    setShippingInfo((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, editing: true } : item
      )
    );
  };

  const cancelEdit = (index) => {
    setShippingInfo((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, editing: false } : item
      )
    );
  };

  const saveShippingInfo = async (info, index) => {
    try {
      if (!info.id) {
        throw new Error("Document ID is missing.");
      }
      const docRef = doc(db, "users", user.uid, "ShippingInfo", info.id);
      await updateDoc(docRef, {
        name: info.name,
        number: info.number,
        flat: info.flat,
        street: info.street,
        locality: info.locality,
        city: info.city,
        state: info.state,
        pinCode: info.pinCode,
      });

      setShippingInfo((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? { ...item, ...info, editing: false } // Update local state
            : item
        )
      );

      toast.success("Shipping info updated successfully!");
    } catch (error) {
      console.error("Error updating shipping info:", error.message);
      toast.error("Failed to update shipping info. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    setNewShippingInfo((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user) {
      const userOrderRef = collection(db, "users", user.uid, "ShippingInfo");
      const orderData = {
        ...newShippingInfo,
      };

      try {
        const docRef = await addDoc(userOrderRef, orderData);
        toast.success("Shipping information saved successfully.");
        setShowForm(false);

        // Add the new address to the state with its ID
        setShippingInfo((prev) => [
          ...prev,
          {
            id: docRef.id,
            ...orderData,
          },
        ]);

        // Reset the form
        setNewShippingInfo({
          name: "",
          number: "",
          flat: "",
          street: "",
          locality: "",
          city: "",
          state: "",
          pinCode: "",
          email: "",
        });

        setAddressExpanded(false);
      } catch (error) {
        toast.error("Error saving address data: " + error.message);
      }
    } else {
      toast.error("Please login to save shipping information.");
    }
  };

  const handleAddAddressClick = () => {
    setShowForm(!showForm);
    if (!showForm && user) {
      setNewShippingInfo((prev) => ({
        ...prev,
        name: user.displayName || "",
        email: user.email || "",
      }));
    }
    setAddressExpanded(showForm ? false : true);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser); // Set the authenticated user
        fetchShippingInfo(currentUser.uid); // Fetch shipping info for the user
      } else {
        setUser(null);
        setShippingInfo([]); // Clear shipping info if user logs out
      }
      setLoading(false); // Stop loading after user state is determined
    });

    // Clean up the listener
    return () => unsubscribe();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (!user) return <div>Please log in to view your shipping information.</div>;

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="shipping-section"
    >
      <h2>Shipping Info</h2>

      {/* Add New Address Button */}
      <button className="add-address-button" onClick={handleAddAddressClick}>
        {showForm ? "Cancel" : "Add New Address"}
      </button>

      {/* Add Address Form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            className="address-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="form-grid">
              <input
                type="text"
                name="name"
                value={newShippingInfo.name}
                onChange={handleInputChange}
                placeholder="Full Name"
                required
              />
              <input
                type="email"
                name="email"
                value={newShippingInfo.email}
                onChange={handleInputChange}
                placeholder="Email Address"
              />
              <input
                type="tel"
                name="number"
                value={newShippingInfo.number}
                onChange={handleInputChange}
                placeholder="Phone Number"
                required
              />
              <input
                type="text"
                name="flat"
                value={newShippingInfo.flat}
                onChange={handleInputChange}
                placeholder="Flat No / Building Name"
                required
              />
              <input
                type="text"
                name="street"
                value={newShippingInfo.street}
                onChange={handleInputChange}
                placeholder="Street Name"
                required
              />
              <input
                type="text"
                name="locality"
                value={newShippingInfo.locality}
                onChange={handleInputChange}
                placeholder="Locality"
                required
              />
              <input
                type="text"
                name="city"
                value={newShippingInfo.city}
                onChange={handleInputChange}
                placeholder="City"
                required
              />
              <input
                type="text"
                name="pinCode"
                value={newShippingInfo.pinCode}
                onChange={handleInputChange}
                placeholder="PIN Code"
                required
              />
              <select
                name="state"
                value={newShippingInfo.state}
                onChange={handleInputChange}
                required
              >
                <option value="">Select State</option>
                <option value="AN">Andaman and Nicobar Islands</option>
                <option value="AP">Andhra Pradesh</option>
                <option value="AR">Arunachal Pradesh</option>
                <option value="AS">Assam</option>
                <option value="BR">Bihar</option>
                <option value="CH">Chandigarh</option>
                <option value="CT">Chhattisgarh</option>
                <option value="DN">Dadra and Nagar Haveli</option>
                <option value="DD">Daman and Diu</option>
                <option value="DL">Delhi</option>
                <option value="GA">Goa</option>
                <option value="GJ">Gujarat</option>
                <option value="HR">Haryana</option>
                <option value="HP">Himachal Pradesh</option>
                <option value="JK">Jammu and Kashmir</option>
                <option value="JH">Jharkhand</option>
                <option value="KA">Karnataka</option>
                <option value="KL">Kerala</option>
                <option value="LA">Ladakh</option>
                <option value="LD">Lakshadweep</option>
                <option value="MP">Madhya Pradesh</option>
                <option value="MH">Maharashtra</option>
                <option value="MN">Manipur</option>
                <option value="ML">Meghalaya</option>
                <option value="MZ">Mizoram</option>
                <option value="NL">Nagaland</option>
                <option value="OR">Odisha</option>
                <option value="PY">Puducherry</option>
                <option value="PB">Punjab</option>
                <option value="RJ">Rajasthan</option>
                <option value="SK">Sikkim</option>
                <option value="TN">Tamil Nadu</option>
                <option value="TG">Telangana</option>
                <option value="TR">Tripura</option>
                <option value="UP">Uttar Pradesh</option>
                <option value="UT">Uttarakhand</option>
                <option value="WB">West Bengal</option>
              </select>
            </div>
            <button type="submit">Save Address</button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* List of saved addresses */}
      <div className="shipping-info-li-container">
        {shippingInfo.map((info, index) => (
          <li className="shipping-info-li" key={info.id}>
            {info.editing ? (
              <div className="shipping-form-grid">
                <label>
                  Name:
                  <input
                    type="text"
                    value={info.name}
                    placeholder="Name"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, name: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Number:
                  <input
                    type="text"
                    value={info.number}
                    placeholder="Number"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, number: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Flat:
                  <input
                    type="text"
                    value={info.flat}
                    placeholder="Flat"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, flat: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Street:
                  <input
                    type="text"
                    value={info.street}
                    placeholder="Street"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, street: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Locality:
                  <input
                    type="text"
                    value={info.locality}
                    placeholder="Locality"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, locality: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  City:
                  <input
                    type="text"
                    value={info.city}
                    placeholder="City"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, city: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  State:
                  <input
                    type="text"
                    value={info.state}
                    placeholder="State"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, state: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <label>
                  Pin Code:
                  <input
                    type="text"
                    value={info.pinCode}
                    placeholder="Pin Code"
                    onChange={(e) =>
                      setShippingInfo((prev) =>
                        prev.map((item, idx) =>
                          idx === index
                            ? { ...item, pinCode: e.target.value }
                            : item
                        )
                      )
                    }
                  />
                </label>
                <div className="button-container">
                  <button
                    onClick={() => saveShippingInfo(info, index)}
                    className="save-button"
                  >
                    Save
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => cancelEdit(index)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="shipping-info-grid">
                <p>
                  <strong>Name:</strong> {info.name}
                </p>
                <p>
                  <strong>Phn. No.:</strong> {info.number}
                </p>
                <p>
                  <strong>Flat:</strong> {info.flat}
                </p>
                <p>
                  <strong>Street:</strong> {info.street}
                </p>
                <p>
                  <strong>Locality:</strong> {info.locality}
                </p>
                <p>
                  <strong>City:</strong> {info.city}
                </p>
                <p>
                  <strong>State:</strong> {info.state}
                </p>
                <p>
                  <strong>Pin Code:</strong> {info.pinCode}
                </p>
                <button
                  className="shipping-edit-button1"
                  onClick={() => enableEditing(index)}
                >
                  Edit
                </button>
              </div>
            )}
          </li>
        ))}
      </div>
    </motion.div>
  );
}

export default Address;
